import type { Message } from '../../api/types'
import { db } from '../../db/db'

export interface LocalSearchFilters {
  channelId?: string
  authorId?: string
  /** ISO date-time bounds (from inclusive, to exclusive). */
  from?: string
  to?: string
}

/** Case-insensitive substring match over messages stored on this device, newest first. */
export async function searchLocalMessages(query: string, filters: LocalSearchFilters = {}, limit = 30): Promise<Message[]> {
  const needle = query.trim().toLowerCase()
  if (!needle) return []
  const collection = filters.channelId ? db.messages.where('channelId').equals(filters.channelId) : db.messages.toCollection()
  const matches = await collection
    .filter(
      (m) =>
        !m.deletedAt &&
        (!filters.authorId || m.authorId === filters.authorId) &&
        (!filters.from || m.createdAt >= filters.from) &&
        (!filters.to || m.createdAt < filters.to) &&
        (m.body.toLowerCase().includes(needle) || m.attachments.some((a) => a.fileName.toLowerCase().includes(needle))),
    )
    .toArray()
  return matches.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)).slice(0, limit)
}

/** Build a [[term]] snippet around the first local match so both result kinds render the same way. */
export function localSnippet(body: string, query: string, radius = 60): string {
  const needle = query.trim()
  if (!needle) return body
  const index = body.toLowerCase().indexOf(needle.toLowerCase())
  if (index < 0) return body.slice(0, radius * 2)
  const start = Math.max(0, index - radius)
  const end = Math.min(body.length, index + needle.length + radius)
  const plain = (s: string) => s.replaceAll('[[', '[ [').replaceAll(']]', '] ]')
  return `${start > 0 ? '…' : ''}${plain(body.slice(start, index))}[[${plain(body.slice(index, index + needle.length))}]]${plain(body.slice(index + needle.length, end))}${end < body.length ? '…' : ''}`
}

export function groupByChannel<T extends { channelId: string }>(items: T[]): { channelId: string; items: T[] }[] {
  const groups = new Map<string, T[]>()
  for (const item of items) {
    const list = groups.get(item.channelId)
    if (list) list.push(item)
    else groups.set(item.channelId, [item])
  }
  return [...groups.entries()].map(([channelId, list]) => ({ channelId, items: list }))
}
